import React, { useCallback } from 'react';
import { Printer } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import { Card } from 'shadcn/components/ui/Card';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { cn } from 'shadcn/lib/utils';

import { formatTotalPalletsLabel, isValidTagsPerPallet } from './printLabelsUtils';

const NOOP = () => {};

const TAGS_PER_PALLET_INPUT_ID = 'tags-per-pallet';

/**
 * @param {Object} props
 * @param {string} [props.className]
 * @param {boolean} [props.isPrinting]
 * @param {number} props.numPallets
 * @param {(tagsPerPallet: number) => void} [props.onPrintAllPalletTags]
 * @param {(event: React.ChangeEvent<HTMLInputElement>) => void} props.onTagsPerPalletChange
 * @param {number | ''} props.tagsPerPallet
 */
function PrintPalletTagsCard({
    className,
    isPrinting = false,
    numPallets,
    onPrintAllPalletTags = NOOP,
    onTagsPerPalletChange,
    tagsPerPallet,
}) {
    const totalPalletsLabel = formatTotalPalletsLabel(numPallets);
    const canPrint = isValidTagsPerPallet(tagsPerPallet);

    const handlePrintAllPalletTags = useCallback(() => {
        if (!canPrint) {
            return;
        }

        onPrintAllPalletTags(tagsPerPallet);
    }, [canPrint, onPrintAllPalletTags, tagsPerPallet]);

    return (
        <Card className={cn('gap-0 border py-0 shadow-none', className)}>
            <div className="flex items-center gap-1 border-b bg-muted/30 px-3 py-1.5 text-sm">
                <span className="font-semibold leading-none">Print Pallet Tags</span>
                <span className="text-xs text-muted-foreground">- {totalPalletsLabel}</span>
            </div>
            <div className="px-3 py-2">
                <Label className="text-xs leading-none text-muted-foreground" htmlFor={TAGS_PER_PALLET_INPUT_ID}>
                    Number of Tags per Pallet
                </Label>
                <div className="mt-1 flex items-end gap-2">
                    <Input
                        id={TAGS_PER_PALLET_INPUT_ID}
                        className="h-8 w-20 text-sm"
                        min={1}
                        step={1}
                        type="number"
                        value={tagsPerPallet}
                        onChange={onTagsPerPalletChange}
                    />
                    <Button
                        className="ml-auto h-8 shrink-0"
                        disabled={isPrinting || !canPrint}
                        size="sm"
                        type="button"
                        variant="outline"
                        onClick={handlePrintAllPalletTags}
                    >
                        <Printer aria-hidden className="size-4" />
                        Print All Pallet Tags
                    </Button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Typically 4 - one per side of the pallet</p>
            </div>
        </Card>
    );
}

export { NOOP, PrintPalletTagsCard };
